import Reaction from './Reaction';
import ReactionPredictor from './ReactionPredictor';
import { buildReactionEquationText, createPairKey } from '../utils/reactionUtils';

/**
 * Holds the catalogue of substances and decides what happens when any two are mixed:
 * a hand-written famous reaction if there is one, otherwise the ReactionPredictor's result.
 * Results are cached, so mixing the same pair twice returns the same Reaction.
 */
export default class ReactionEngine {
  constructor(substances = [], curatedReactions = []) {
    this.substancesById = new Map(substances.map((substance) => [substance.id, substance]));
    this.predictor = new ReactionPredictor(this.substancesById);
    this.reactionsByKey = new Map();
    this.curatedKeys = new Set();
    curatedReactions.forEach((definition) => this.registerCuratedReaction(definition));
  }

  registerCuratedReaction(definition) {
    const [firstId, secondId] = definition.pair;
    const substances = [firstId, secondId].map((id) => {
      const substance = this.getSubstance(id);
      if (!substance) throw new Error(`Curated reaction "${definition.title}" uses unknown substance "${id}".`);
      return substance;
    });
    const equationText = definition.products ? buildReactionEquationText(substances, definition.products) : '';
    const reaction = new Reaction({
      reactantIds: [firstId, secondId],
      title: definition.title,
      explanation: definition.explanation,
      funFact: definition.funFact,
      safetyNote: definition.safetyNote,
      effect: definition.effect,
      productName: definition.productName ?? definition.mixtureName,
      equationText,
      conditions: definition.conditions,
      isMixture: Boolean(definition.mixtureName),
    });
    const key = reaction.getKey();
    this.reactionsByKey.set(key, reaction);
    this.curatedKeys.add(key);
  }

  getSubstance(substanceId) {
    return this.substancesById.get(substanceId) ?? null;
  }

  getAllSubstances() {
    return [...this.substancesById.values()];
  }

  getCuratedReactions() {
    return [...this.curatedKeys].map((key) => this.reactionsByKey.get(key));
  }

  isCurated(firstId, secondId) {
    return this.curatedKeys.has(createPairKey(firstId, secondId));
  }

  /**
   * Mixes two different substances and always returns a Reaction.
   */
  mix(firstId, secondId) {
    if (!this.getSubstance(firstId) || !this.getSubstance(secondId)) {
      throw new Error(`Cannot mix unknown substances "${firstId}" and "${secondId}".`);
    }
    const key = createPairKey(firstId, secondId);
    if (!this.reactionsByKey.has(key)) {
      this.reactionsByKey.set(key, new Reaction(this.predictor.predict(firstId, secondId)));
    }
    return this.reactionsByKey.get(key);
  }

  /**
   * Every possible pair of different substances, each counted once.
   */
  getAllPairs() {
    const ids = [...this.substancesById.keys()];
    return ids.flatMap((firstId, index) => ids.slice(index + 1).map((secondId) => [firstId, secondId]));
  }
}
